// Loading placeholders shaped like the screens they stand in for, so a page
// fills in place instead of jumping from a spinner to content. Each one is a
// single status region with a readable label; the grey shapes are hidden from
// screen readers.
import type { ReactNode } from 'react'
import { Card, Skeleton } from './layout.tsx'
import { cx } from './cx.ts'

function Status({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <div role="status" aria-label={label} className={className}>
      <span className="sr-only">{label}…</span>
      {children}
    </div>
  )
}

const WIDTHS = ['w-2/5', 'w-3/5', 'w-1/2', 'w-2/3', 'w-1/3']

/** Rows of a table, for inside a list card (under its search bar). */
export function SkeletonTable({
  rows = 8,
  cols = 4,
  label = 'Loading',
}: {
  rows?: number
  cols?: number
  label?: string
}) {
  return (
    <Status label={label}>
      <div className="flex gap-4 border-b border-border px-4 py-3">
        {Array.from({ length: cols }, (_, c) => (
          <Skeleton key={c} className={cx('h-3', c === 0 ? 'flex-[2]' : 'flex-1')} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 border-b border-border px-4 py-3.5">
          <div className="flex-[2] space-y-1.5">
            <Skeleton className={cx('h-3.5', WIDTHS[r % WIDTHS.length])} />
            <Skeleton className="h-2.5 w-1/4" />
          </div>
          {Array.from({ length: cols - 1 }, (_, c) => (
            <div key={c} className="flex flex-1 justify-end">
              <Skeleton className="h-3.5 w-16" />
            </div>
          ))}
        </div>
      ))}
    </Status>
  )
}

/** A page title with its subtitle. */
function SkeletonTitle() {
  return (
    <div className="mb-6 space-y-2">
      <Skeleton className="h-7 w-52" />
      <Skeleton className="h-3.5 w-72 max-w-full" />
    </div>
  )
}

/** A detail page: title, summary tiles, then a list. */
export function SkeletonDetail({ label = 'Loading' }: { label?: string }) {
  return (
    <Status label={label}>
      <SkeletonTitle />
      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Card key={i} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-6 w-28" />
          </Card>
        ))}
      </div>
      <Card className="p-0">
        <SkeletonRows rows={5} />
      </Card>
    </Status>
  )
}

function SkeletonRows({ rows }: { rows: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className="flex items-center gap-4 border-b border-border px-4 py-3.5 last:border-0"
        >
          <Skeleton className={cx('h-3.5', WIDTHS[r % WIDTHS.length])} />
          <Skeleton className="ml-auto h-3.5 w-16" />
        </div>
      ))}
    </>
  )
}

/** A settings form: cards of label + field pairs. */
export function SkeletonForm({
  cards = 2,
  fields = 4,
  title = false,
  label = 'Loading',
}: {
  cards?: number
  fields?: number
  title?: boolean
  label?: string
}) {
  return (
    <Status label={label} className="space-y-4">
      {title && <SkeletonTitle />}
      {Array.from({ length: cards }, (_, c) => (
        <Card key={c} className="space-y-4">
          <Skeleton className="h-4 w-40" />
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: fields }, (_, f) => (
              <div key={f} className="space-y-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-10 w-full rounded-xl" />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </Status>
  )
}

/** A whole list page (title plus a table card), for screens that load before their header. */
export function SkeletonPage({ label = 'Loading' }: { label?: string }) {
  return (
    <Status label={label}>
      <SkeletonTitle />
      <Card className="p-0">
        <div className="border-b border-border p-4">
          <Skeleton className="h-10 w-full max-w-md rounded-xl" />
        </div>
        <SkeletonRows rows={6} />
      </Card>
    </Status>
  )
}
